/**
 * Downloading the County Toolkit installer with a workstation's setup code in its name.
 *
 * The installer is one shared artifact: the toolkit's pipeline publishes a single .exe per merge
 * and `latest.yml` names it, so it cannot be rebuilt per desk (and once it is code-signed, it
 * cannot be rewritten per desk either). What *can* differ per desk is the name the browser saves it
 * under - a filename is not covered by a signature - so the code rides there, and the toolkit reads
 * it back on its first boot.
 *
 * A `download` attribute is ignored cross-origin, which is why this fetches the bytes and saves a
 * Blob rather than pointing a link at the blob container. That needs a CORS rule on the container;
 * where there is not one, `installerUrl()` is the plain link the dialog falls back to, and the code
 * is typed into the toolkit's General tab instead. The guided path degrades to the manual one.
 *
 * Design: docs/plans/GUIDED-ENROLLMENT-PLAN.md in the regov-county-toolkit repo.
 */
import { countySlug } from './clients/client-config';

/** Where the toolkit's own auto-updater reads from - `build.publish` in its package.json. */
export const TOOLKIT_FEED_URL = 'https://tmcpublicartifacts.blob.core.windows.net/regov-county-toolkit';

/**
 * `RegovCountyToolkit-Setup-<county>-<code>.exe`.
 *
 * No version in the name, deliberately: the app updates itself from the feed within the day, and
 * leaving it out keeps the code the only variable part, so the toolkit's parser stays a regex
 * rather than a guess about which hyphen-separated run is which.
 */
export const enrollmentFileName = ({ county, code }) => `RegovCountyToolkit-Setup-${county}-${code}.exe`;

/**
 * Pull the current installer's real name out of latest.yml.
 *
 * Read with a regex rather than a YAML parser: this is two known keys written by electron-builder,
 * and the alternative is a dependency for one line. `path` is the unencoded name (it has spaces in
 * it, which is why callers must encode it); `version` is only shown to the admin.
 */
export const installerPathFrom = (latestYml) => {
  const match = /^path:\s*(.+?)\s*$/m.exec(String(latestYml || ''));
  return match ? match[1] : null;
};

export const installerVersionFrom = (latestYml) => {
  const match = /^version:\s*(.+?)\s*$/m.exec(String(latestYml || ''));
  return match ? match[1] : null;
};

export const installerUrl = (path) => `${TOOLKIT_FEED_URL}/${encodeURIComponent(path)}`;

/** Saves a Blob under a name of our choosing. Same-origin by construction, so `download` applies. */
const saveBlobAs = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Revoking immediately cancels the download in some browsers; one tick is enough for the click
  // to have been taken.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/** Which build the feed is currently serving. Separated so the dialog can show it before the click. */
export const fetchLatestInstaller = async ({ fetchImpl = fetch } = {}) => {
  const res = await fetchImpl(`${TOOLKIT_FEED_URL}/latest.yml`, { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`The toolkit download feed answered ${res.status}.`);
  }
  const body = await res.text();
  const path = installerPathFrom(body);
  if (!path) {
    throw new Error('The toolkit download feed did not name an installer.');
  }
  return { path, version: installerVersionFrom(body), url: installerUrl(path) };
};

/**
 * Fetch the installer and save it under the desk's own name.
 *
 * Streamed rather than awaited as one blob so the admin sees movement: this is well over 100MB on a
 * county connection, and a button that does nothing visible for two minutes is a button people
 * click again. Falls back to res.blob() where the response has no readable body.
 */
export const downloadToolkitInstaller = async ({
  county, code, onProgress = () => {}, fetchImpl = fetch, save = saveBlobAs,
} = {}) => {
  if (!county || !code) {
    throw new Error('A county and a setup code are both needed to name the download.');
  }
  const latest = await fetchLatestInstaller({ fetchImpl });

  const res = await fetchImpl(latest.url);
  if (!res.ok) {
    throw new Error(`The installer download answered ${res.status}.`);
  }

  const total = Number(res.headers && res.headers.get ? res.headers.get('content-length') : 0) || 0;
  let blob;

  if (res.body && res.body.getReader) {
    const reader = res.body.getReader();
    const chunks = [];
    let received = 0;
    let done = false;
    while (!done) {
      // eslint-disable-next-line no-await-in-loop
      const step = await reader.read();
      done = step.done;
      if (step.value) {
        chunks.push(step.value);
        received += step.value.length;
        onProgress(total ? Math.round((received / total) * 100) : null);
      }
    }
    blob = new Blob(chunks, { type: 'application/octet-stream' });
  } else {
    blob = await res.blob();
  }

  save(blob, enrollmentFileName({ county, code }));
  return { ...latest, filename: enrollmentFileName({ county, code }) };
};

export default {
  TOOLKIT_FEED_URL,
  countySlug,
  enrollmentFileName,
  installerPathFrom,
  installerVersionFrom,
  installerUrl,
  fetchLatestInstaller,
  downloadToolkitInstaller,
};
